import {
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  ROOTLY,
} from '@elementary/common-ui/constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RootlyIntegrationForm } from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type Props = {
  onUpdateSuccess?: () => void;
};

const useRootlyIntegration = ({ onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const rootlyQuery = useQuery([ROOTLY], () => api.fetchRootly(), {});

  const rootlyIntegrationMutation = useMutation(
    (data: RootlyIntegrationForm) =>
      rootlyQuery.data?.integrated
        ? api.updateRootlyIntegration({ sources: data.sources })
        : api.setRootlyIntegration({ sources: data.sources }),
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([ROOTLY]);
        queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
        openSnackbar({
          message: 'Successfully connected to your Rootly',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_ROOTLY_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_ROOTLY_INTEGRATION_FAILURE,
        }),
    },
  );

  return {
    rootlyQuery,
    rootlyIntegrationMutation,
    isRootlyConnected: !!rootlyQuery.data?.integrated,
  };
};

export default useRootlyIntegration;
